
import { useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const GtmInitializer = () => {
  // Fetch GTM container ID from site settings
  const { data: siteSettings } = useQuery({
    queryKey: ['gtm-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_key, setting_value')
        .eq('setting_key', 'gtm_container_id')
        .single();
      
      if (error) throw error;
      return data?.setting_value || '';
    }
  });

  useEffect(() => {
    // Only proceed if we have a GTM container ID
    if (!siteSettings) {
      console.log('GTM container ID not found in site settings');
      return;
    }

    const GTM_ID = siteSettings;
    console.log('Initializing GTM with container ID:', GTM_ID);

    // Check if GTM is already initialized to avoid duplicates
    if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${GTM_ID}"]`)) {
      console.log('GTM already initialized');
      return;
    }

    // --- Step 1: Set Default Consent State (as per Google Consent Mode v2) ---
    const consentScript = document.createElement('script');
    consentScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('consent', 'default', {
        'ad_storage': 'denied',
        'analytics_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'wait_for_update': 500
      });
    `;
    document.head.appendChild(consentScript);

    // --- Step 2: Inject the main GTM container script ---
    const gtmScript = document.createElement('script');
    gtmScript.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${GTM_ID}');
    `;
    document.head.appendChild(gtmScript);

    // --- Step 3: Inject the GTM noscript iframe for browsers with JS disabled ---
    const gtmNoScript = document.createElement('noscript');
    gtmNoScript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;
    document.body.insertBefore(gtmNoScript, document.body.firstChild);

    // Store references for cleanup
    const cleanup = () => {
      try {
        if (consentScript.parentNode) {
          consentScript.parentNode.removeChild(consentScript);
        }
        if (gtmScript.parentNode) {
          gtmScript.parentNode.removeChild(gtmScript);
        }
        if (gtmNoScript.parentNode) {
          gtmNoScript.parentNode.removeChild(gtmNoScript);
        }
      } catch (error) {
        console.log('GTM cleanup error:', error);
      }
    };

    return cleanup;
  }, [siteSettings]);

  return null; // This component does not render anything visible
};

export default GtmInitializer;
